// DealSource API file

import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../environment";

const BASE_URL = `${API_BASE_PATH}/dealsource`;

const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken") || "";
  return {
    Authorization: token,
    Accept: "application/json",
  };
};

const handleAxiosError = (
  error: any,
  fallbackMessage = "Something went wrong"
) => {
  if (axios.isAxiosError(error)) {
    const message =
      error.response?.data?.details ||
      error.response?.data?.message ||
      fallbackMessage;

    toast.error(message);
  } else {
    toast.error(fallbackMessage);
  }
};

export interface DealSource {
  deal_source_id?: string;
  deal_source_nema: string;
  status: string;
  created_at?: string;
  updated_at?: string;
  client?: {
    client_id: string;
  };
}

// ✅ Create Deal Source
export const createDealSource = async (data: DealSource): Promise<DealSource | null> => {
  try {
    const form = new URLSearchParams();
    form.append("deal_source_nema", data.deal_source_nema);
    form.append("status", data.status);

    const res = await axios.post(`${BASE_URL}/create/`, form, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        ...getAuthHeaders(),
      },
    });

    toast.success("Deal Source created successfully");
    return res.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to create Deal Source";
    toast.error(errorMessage);
    return null;
  }
};

// ✅ Update Deal Source
export const updateDealSource = async (
  deal_source_id: string,
  data: DealSource
): Promise<DealSource | null> => {
  try {
    const form = new URLSearchParams();
    form.append("deal_source_nema", data.deal_source_nema);
    form.append("status_", data.status);

    const res = await axios.put(`${BASE_URL}/${deal_source_id}/update`, form, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        ...getAuthHeaders(),
      },
    });

    toast.success("Deal Source updated successfully");
    return res.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to update Deal Source";
    toast.error(errorMessage);
    return null;
  }
};

// ✅ Get Deal Source by ID
export const fetchDealSourceById = async (deal_source_id: string): Promise<DealSource | null> => {
  try {
    const res = await axios.get(`${BASE_URL}/${deal_source_id}`, {
      headers: getAuthHeaders(),
    });
    return res.data.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to fetch Deal Source ID.";
    toast.error(errorMessage);
    return null;
  }
};

// ✅ Delete Deal Source
export const deleteDealSource = async (deal_source_id: string): Promise<void> => {
  try {
    await axios.delete(`${BASE_URL}/${deal_source_id}/delete`, {
      headers: getAuthHeaders(),
    });
    toast.success("Deal Source deleted successfully");
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to delete Deal Source";
    toast.error(errorMessage);
  }
};

// ✅ Get all Deal Sources
export const fetchAllDealSources = async (): Promise<DealSource[]> => {
  try {
    const res = await axios.get(`${BASE_URL}/getall/all_source`, {
      headers: getAuthHeaders(),
    });

    if (res.data?.detail?.includes("not found")) {
      throw new Error("Deal Source not found");
    }

    return res.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to fetch Deal Source.";
    toast.error(errorMessage);
    return [];
  }
};

// ✅ Get all Deal Sources
export const fetchAllDealSourcesActive = async (): Promise<DealSource[]> => {
  try {
    const res = await axios.get(`${BASE_URL}/getall/all_source/active`, {
      headers: getAuthHeaders(),
    });

    if (res.data?.detail?.includes("not found")) {
      throw new Error("Deal Source not found");
    }

    return res.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to fetch Deal Sources";
    toast.error(errorMessage);
    return [];
  }
};
/* =====================================================
   EXPORT – ALL
===================================================== */

// ✅ Export all Deal Sources – Excel
export const exportDealSourceExcel = async (): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/dealsource_export_all_excel`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Deal sources exported as Excel successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export deal sources (Excel)");
    throw error;
  }
};

// ✅ Export all Deal Sources – PDF
export const exportDealSourcePDF = async (): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/dealsource_export_all_pdf`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Deal sources exported as PDF successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export deal sources (PDF)");
    throw error;
  }
};

/* =====================================================
   EXPORT – SELECTED
===================================================== */

// ✅ Export selected Deal Sources – Excel
export const exportSelectedDealSourceExcel = async (
  ids: string[]
): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/dealsource_export_selected_excel`,
      {
        params: { ids: ids.join(",") },
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Selected deal sources exported as Excel successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(
      error,
      "Failed to export selected deal sources (Excel)"
    );
    throw error;
  }
};

// ✅ Export selected Deal Sources – PDF
export const exportSelectedDealSourcePDF = async (
  ids: string[]
): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/dealsource_export_selected_pdf`,
      {
        params: { ids: ids.join(",") },
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Selected deal sources exported as PDF successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(
      error,
      "Failed to export selected deal sources (PDF)"
    );
    throw error;
  }
};

/* =====================================================
   BULK DELETE
===================================================== */

// ✅ Bulk delete Deal Sources
export const deleteDealSourceBulk = async (
  ids: string[]
): Promise<void> => {
  try {
    const res = await axios.delete(
      `${API_BASE_PATH}/dealsource_bulk_delete`,
      {
        params: { ids: ids.join(",") },
        headers: getAuthHeaders(),
      }
    );

    // toast.success(
    //   res.data?.message || "Deal sources deleted successfully"
    // );
  } catch (error: any) {
    if (error?.response?.status === 403) {
      console.error("You are not authorized to delete deal sources.");
    } else {
      handleAxiosError(error, "Failed to delete deal sources");
    }
    throw error;
  }
};

/* =====================================================
   IMPORT – EXCEL
===================================================== */

export interface DealSourceImportResult {
  message: string;
  added: {
    deal_source_name: string;
    deal_source_status: string;
  }[];
  skipped: {
    row: number;
    deal_source_name: string;
    reason: string;
  }[];
}

// ✅ Import Deal Source Excel
export const importDealSourceExcel = async (
  file: File
): Promise<DealSourceImportResult> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const res = await axios.post(
      `${API_BASE_PATH}/dealsource_import_excel`,
      formData,
      {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      }
    );

    toast.success(
      res.data?.message || "Deal source import completed"
    );
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to import deal sources");
    throw error;
  }
};